/**
 * Monaco editor for the Rich web automation script DSL (Module 36).
 *
 * - JSON language mode with the server-provided DSL JSON Schema attached, so
 *   unknown step types, missing `condition` on `if`, etc. surface as squiggles.
 * - Snippet completions for step types (setVar, if, while, foreach, ...) and
 *   common expression functions (Concat, Round, Split, ...).
 */

import { useEffect, useRef, useState } from 'react';
import { Editor, useMonaco } from '@monaco-editor/react';
import { webAutomationService } from '../../services/webAutomationService';

const DSL_SCHEMA_URI = 'agentops://web-automation/dsl-schema.json';
/** Matches models created for this editor so the schema only applies here. */
const DSL_FILE_MATCH = 'web-automation-dsl*.json';

interface WebAutomationDslEditorProps {
  value: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
  height?: string;
}

const STEP_SNIPPETS: Array<{ label: string; documentation: string; insertText: string }> = [
  {
    label: 'step: setVar',
    documentation: 'Assign a variable from a literal or {{expression}}.',
    insertText:
      '{ "type": "setVar", "name": "${1:total}", "value": "{{${2:Round(ToNumber(subtotal) * 1.08, 2)}}}" }',
  },
  {
    label: 'step: if / else',
    documentation: 'Branch on a boolean expression over runtime variables.',
    insertText:
      '{\n  "type": "if",\n  "condition": "${1:ToNumber(count) > 0}",\n  "then": [\n    $2\n  ],\n  "else": [\n    $3\n  ]\n}',
  },
  {
    label: 'step: while',
    documentation: 'Repeat steps while the condition stays true (bounded by maxIterations).',
    insertText:
      '{\n  "type": "while",\n  "condition": "${1:not(Contains(status, \'Done\'))}",\n  "maxIterations": ${2:20},\n  "steps": [\n    $3\n  ]\n}',
  },
  {
    label: 'step: foreach',
    documentation: 'Iterate a list (Split CSV, JSON array, or Column of an extracted table).',
    insertText:
      '{\n  "type": "foreach",\n  "items": "${1:Split(ids, \',\')}",\n  "itemVar": "${2:item}",\n  "indexVar": "${3:i}",\n  "steps": [\n    $4\n  ]\n}',
  },
  {
    label: 'step: navigate',
    documentation: 'Open a URL (supports {{expressions}}).',
    insertText: '{ "type": "navigate", "url": "${1:{{siteUrl}}}" }',
  },
  {
    label: 'step: fill',
    documentation: 'Fill a field located by visible label (or selector).',
    insertText: '{ "type": "fill", "label": "${1:Amount}", "value": "${2:{{total}}}" }',
  },
  {
    label: 'step: clickText',
    documentation: 'Click the element with the given visible text.',
    insertText: '{ "type": "clickText", "text": "${1:Continue}" }',
  },
  {
    label: 'step: assertText',
    documentation: 'Fail the run unless the text is visible on the page.',
    insertText: '{ "type": "assertText", "expectedText": "${1:Success}" }',
  },
  {
    label: 'step: extractFields',
    documentation: 'Capture labelled field values into runtime variables + run output.',
    insertText: '{ "type": "extractFields", "labels": ["${1:Confirmation #}"] }',
  },
];

const FUNCTION_COMPLETIONS = [
  'Concat', 'Length', 'Substring', 'Replace', 'Trim', 'ToUpper', 'ToLower',
  'Contains', 'StartsWith', 'EndsWith', 'IndexOf', 'Split', 'Join',
  'PadLeft', 'PadRight', 'RegexMatch', 'RegexReplace', 'IsMatch',
  'ToNumber', 'ToInt', 'ToString', 'Mod', 'Count', 'GetItem', 'Column',
  'Var', 'IsSet', 'Coalesce',
  'Abs', 'Min', 'Max', 'Round', 'Floor', 'Ceiling', 'Pow', 'Sqrt',
];

export const WebAutomationDslEditor = ({
  value,
  onChange,
  readOnly = false,
  height = '420px',
}: WebAutomationDslEditorProps) => {
  const monaco = useMonaco();
  const [schema, setSchema] = useState<object | null>(null);
  const completionDisposable = useRef<{ dispose: () => void } | null>(null);

  useEffect(() => {
    let cancelled = false;
    webAutomationService
      .getDslSchema()
      .then((s) => {
        if (!cancelled) setSchema(s);
      })
      .catch(() => {
        // Editor still works without schema diagnostics.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!monaco || !schema) return;

    // monaco-editor >= 0.52 exposes JSON defaults at the top-level `json`
    // namespace; older loader builds keep them under `languages.json`.
    interface JsonDefaults {
      diagnosticsOptions: { schemas?: Array<{ uri: string; fileMatch?: string[]; schema?: object }> };
      setDiagnosticsOptions: (options: object) => void;
    }
    const monacoAny = monaco as unknown as {
      json?: { jsonDefaults?: JsonDefaults };
      languages: { json?: { jsonDefaults?: JsonDefaults } };
    };
    const jsonDefaults = monacoAny.json?.jsonDefaults ?? monacoAny.languages.json?.jsonDefaults;
    if (!jsonDefaults) return;
    const existing = jsonDefaults.diagnosticsOptions.schemas ?? [];
    jsonDefaults.setDiagnosticsOptions({
      ...jsonDefaults.diagnosticsOptions,
      validate: true,
      schemas: [
        ...existing.filter((s) => s.uri !== DSL_SCHEMA_URI),
        {
          uri: DSL_SCHEMA_URI,
          fileMatch: [DSL_FILE_MATCH],
          schema,
        },
      ],
    });
  }, [monaco, schema]);

  useEffect(() => {
    if (!monaco || readOnly) return;

    completionDisposable.current?.dispose();
    completionDisposable.current = monaco.languages.registerCompletionItemProvider('json', {
      triggerCharacters: ['{', '"'],
      provideCompletionItems: (model, position) => {
        if (!model.uri.path.includes('web-automation-dsl')) {
          return { suggestions: [] };
        }
        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn,
        };

        const stepSuggestions = STEP_SNIPPETS.map((snippet) => ({
          label: snippet.label,
          kind: monaco.languages.CompletionItemKind.Snippet,
          documentation: snippet.documentation,
          insertText: snippet.insertText,
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          range,
        }));

        const functionSuggestions = FUNCTION_COMPLETIONS.map((fn) => ({
          label: `fn: ${fn}`,
          kind: monaco.languages.CompletionItemKind.Function,
          documentation: `DSL expression function ${fn}(...) — usable inside {{...}}, condition and items.`,
          insertText: `${fn}($1)`,
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          range,
        }));

        return { suggestions: [...stepSuggestions, ...functionSuggestions] };
      },
    });

    return () => {
      completionDisposable.current?.dispose();
      completionDisposable.current = null;
    };
  }, [monaco, readOnly]);

  return (
    <div className="rounded border border-border overflow-hidden">
      <Editor
        height={height}
        defaultLanguage="json"
        path={`web-automation-dsl-${readOnly ? 'view' : 'edit'}.json`}
        value={value}
        onChange={(val) => onChange?.(val || '')}
        loading={
          <div className="flex justify-center py-12">
            <div className="h-7 w-7 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        }
        options={{
          readOnly,
          minimap: { enabled: false },
          fontSize: 13,
          wordWrap: 'on',
          lineNumbers: 'on',
          scrollBeyondLastLine: false,
          formatOnPaste: true,
          tabSize: 2,
        }}
      />
    </div>
  );
};
